/* Fatal size finder: height + weight -> fitted and relaxed size.
   The model is the size chart as a grid (closest height row x the weight
   column at or below the rider's weight). Theme settings > Fatal can override it with JSON produced by
   tools/fit-model/fit_model.py once fit-check feedback comes in. */
(function () {
  if (customElements.get('fatal-size-finder')) return;

  const SIZES = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

  // Fatal size chart, cycling jerseys + bib shorts. null = outside the chart.
  const DEFAULT_MODEL = {
    version: 1,
    source: 'Fatal size chart',
    heights_cm: [160, 165, 170, 175, 180, 185, 190],
    weights_kg: [55, 60, 65, 70, 75, 80, 85, 90],
    grid: [
      ['S', 'M', 'L', 'XL', null, null, null, null],
      ['S', 'S', 'M', 'L', 'XL', null, null, null],
      ['S', 'S', 'M', 'L', 'XL', '2XL', null, null],
      [null, 'S', 'M', 'L', 'XL', '2XL', '3XL', null],
      [null, null, 'S', 'M', 'L', 'XL', '2XL', '3XL'],
      [null, null, null, 'M', 'L', 'XL', '2XL', '3XL'],
      [null, null, null, null, 'L', 'XL', '2XL', '3XL'],
    ],
  };

  const LABELS = {
    'extra extra small': '2XS', xxs: '2XS', '2xs': '2XS',
    'extra small': 'XS', xs: 'XS',
    small: 'S', s: 'S',
    medium: 'M', m: 'M',
    large: 'L', l: 'L',
    'extra large': 'XL', xl: 'XL',
    'extra extra large': '2XL', xxl: '2XL', '2xl': '2XL',
    xxxl: '3XL', '3xl': '3XL',
  };
  const normalizeSize = (v) => LABELS[String(v).trim().toLowerCase()] || String(v).trim().toUpperCase();

  function nearestIndex(list, value) {
    let best = 0;
    list.forEach((v, i) => {
      // Ties keep the shorter row, which never gives the smaller size.
      if (Math.abs(v - value) < Math.abs(list[best] - value)) best = i;
    });
    return best;
  }

  // Weight columns are where a size starts: riders use the column at or below
  // their weight (68 kg reads the 65 kg column). 0.5 kg slack so a rider who
  // enters the chart's own lb figure (154 lb = 69.9 kg) lands on that column.
  function weightIndex(list, value) {
    let idx = 0;
    list.forEach((v, i) => {
      if (v <= value + 0.5) idx = i;
    });
    return idx;
  }

  function recommend(model, heightCm, weightKg) {
    const { heights_cm: hs, weights_kg: ws, grid } = model;
    const r = nearestIndex(hs, heightCm);
    let c = weightIndex(ws, weightKg);
    const hStep = (hs[hs.length - 1] - hs[0]) / (hs.length - 1);
    const wStep = (ws[ws.length - 1] - ws[0]) / (ws.length - 1);
    let outside =
      heightCm < hs[0] - hStep / 2 ||
      heightCm > hs[hs.length - 1] + hStep / 2 ||
      weightKg < ws[0] - 0.5 ||
      weightKg >= ws[ws.length - 1] + wStep;

    const at = (i, j) => (grid[i] && grid[i][j] ? SIZES.indexOf(normalizeSize(grid[i][j])) : -1);
    let fitted = at(r, c);
    if (fitted < 0) {
      // Off the chart for this height. Start from the nearest size in the row.
      outside = true;
      let best = -1;
      grid[r].forEach((v, i) => {
        if (v && (best < 0 || Math.abs(i - c) < Math.abs(best - c))) best = i;
      });
      if (best < 0) return null;
      fitted = at(r, best);
      // Short and heavy: the row's biggest size can be too small. A shorter
      // rider never needs less than a taller rider at the same weight, so take
      // the first taller row that has this weight if it says bigger.
      if (best < c) {
        for (let i = r + 1; i < grid.length; i++) {
          if (at(i, c) >= 0) {
            fitted = Math.max(fitted, at(i, c));
            break;
          }
        }
      }
      // Tall and light, the mirror case: never more than a shorter rider.
      if (best > c) {
        for (let i = r - 1; i >= 0; i--) {
          if (at(i, c) >= 0) {
            fitted = Math.min(fitted, at(i, c));
            break;
          }
        }
      }
    }
    if (fitted < 0) return null;
    return {
      fitted: SIZES[fitted],
      relaxed: SIZES[Math.min(fitted + 1, SIZES.length - 1)],
      relaxedSame: fitted === SIZES.length - 1,
      outside,
    };
  }

  const storage = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  class FatalSizeFinder extends HTMLElement {
    connectedCallback() {
      this.model = DEFAULT_MODEL;
      const override = this.querySelector('script[data-size-model]');
      if (override && override.textContent.trim()) {
        try {
          const m = JSON.parse(override.textContent);
          if (m.heights_cm && m.weights_kg && m.grid) this.model = m;
        } catch (e) {
          console.warn('Fatal size finder: size model JSON in theme settings is invalid, using the chart.', e);
        }
      }

      this.form = this.querySelector('form');
      this.result = this.querySelector('[data-result]');
      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('submit', (e) => e.preventDefault());
      this.querySelectorAll('[data-unit]').forEach((b) =>
        b.addEventListener('click', () => this.setUnits(b.dataset.unit))
      );
      this.querySelectorAll('[data-select]').forEach((b) =>
        b.addEventListener('click', () => this.selectSize(b.dataset.select))
      );

      const saved = storage.get('fatal-size-finder');
      let units = 'imperial';
      if (saved) {
        try {
          const s = JSON.parse(saved);
          units = s.units || units;
          Object.entries(s.values || {}).forEach(([name, v]) => {
            const el = this.form.elements[name];
            if (el) el.value = v;
          });
        } catch (e) { /* ignore */ }
      }
      this.setUnits(units);
    }

    setUnits(units) {
      this.units = units;
      this.querySelectorAll('[data-unit]').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.unit === units))
      );
      this.querySelectorAll('[data-units]').forEach((el) => {
        el.hidden = el.dataset.units !== units;
      });
      this.update();
    }

    read() {
      const n = (name) => parseFloat(this.form.elements[name].value);
      let h;
      let w;
      if (this.units === 'metric') {
        h = n('height_cm');
        w = n('weight_kg');
      } else {
        const ftRaw = this.form.elements.height_ft.value.trim();
        const inRaw = this.form.elements.height_in.value.trim();
        let ft = parseFloat(ftRaw);
        let inches = inRaw === '' ? 0 : parseFloat(inRaw);
        if (inRaw === '' && ft >= 100) {
          // cm typed into the feet box
          h = ft;
        } else {
          if (inRaw === '' && ft >= 48 && ft <= 90) {
            // total inches typed into the feet box (68 = 5'8")
            inches = ft;
            ft = 0;
          } else if (inRaw === '' && /^\d\.\d{1,2}$/.test(ftRaw)) {
            // 5.10 meaning 5'10", not 5.1 feet
            const [whole, part] = ftRaw.split('.');
            ft = Number(whole);
            inches = Number(part);
          }
          if (inches > 11 && ft > 0) return { h: NaN, w: NaN, error: 'inches' };
          h = Number.isFinite(ft) ? (ft * 12 + inches) * 2.54 : NaN;
        }
        w = n('weight_lb') * 0.453592;
      }
      return { h, w };
    }

    update() {
      const values = {};
      ['height_ft', 'height_in', 'weight_lb', 'height_cm', 'weight_kg'].forEach((k) => {
        values[k] = this.form.elements[k].value;
      });
      storage.set('fatal-size-finder', JSON.stringify({ units: this.units, values }));

      const { h, w, error } = this.read();
      const ready = Number.isFinite(h) && Number.isFinite(w);
      const bmi = ready ? w / (h / 100) ** 2 : 0;
      const plausible = ready && h >= 120 && h <= 230 && w >= 30 && w <= 200 && bmi >= 15 && bmi <= 45;
      const prompt = this.querySelector('[data-prompt]');
      this.result.hidden = !plausible;
      prompt.hidden = plausible;
      if (!plausible) {
        // Only complain once both boxes are filled; mid-typing stays quiet.
        let msg = prompt.dataset.prompt;
        if (error === 'inches') msg = prompt.dataset.inches;
        else if (ready && w >= 30 && h >= 120) msg = prompt.dataset.check;
        prompt.textContent = msg;
        return;
      }

      const rec = recommend(this.model, h, w);
      if (!rec) {
        this.result.hidden = true;
        return;
      }
      this.fill('fitted', rec.fitted);
      this.fill('relaxed', rec.relaxed);
      this.querySelector('[data-relaxed-note]').hidden = !rec.relaxedSame;
      this.querySelector('[data-outside]').hidden = !rec.outside;
    }

    findInput(size) {
      const scope = this.closest('product-info') || document;
      const pickers = scope.querySelectorAll('variant-selects input[type="radio"], variant-selects select');
      return Array.from(pickers).find((el) => {
        const name = (el.dataset.optionName || el.name || '').toLowerCase();
        if (!/size/.test(name)) return false;
        if (el.tagName === 'SELECT') {
          return Array.from(el.options).some((o) => normalizeSize(o.value) === size);
        }
        return normalizeSize(el.value) === size;
      });
    }

    fill(kind, size) {
      this.querySelector(`[data-size="${kind}"]`).textContent = size;
      const btn = this.querySelector(`[data-select="${kind}"]`);
      btn.dataset.target = size;
      const input = this.findInput(size);
      const offered = Boolean(input);
      btn.hidden = !offered;
      btn.querySelector('span').textContent = `Select ${size}`;
      this.querySelector(`[data-not-offered="${kind}"]`).hidden = offered;
    }

    selectSize(kind) {
      const size = this.querySelector(`[data-select="${kind}"]`).dataset.target;
      const input = this.findInput(size);
      if (!input) return;
      if (input.tagName === 'SELECT') {
        const opt = Array.from(input.options).find((o) => normalizeSize(o.value) === size);
        input.value = opt.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!input.checked) {
        input.click();
      }
      this.querySelectorAll('[data-select]').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.select === kind))
      );
    }
  }

  customElements.define('fatal-size-finder', FatalSizeFinder);
  window.FatalSizeFinder = { recommend, DEFAULT_MODEL, SIZES, normalizeSize };
})();
